import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

type DonationListInput = Parameters<typeof api.donation.list>[0];
type DeletedDonationListInput = Parameters<typeof api.donation.listDeleted>[0];
type StaffQuoteInput = Parameters<typeof api.campaign.staffQuote>[0];

export const donationKeys = {
  all: ['donations'] as const,
  list: (filters: DonationListInput) => ['donations', 'list', filters] as const,
  detail: (id: string) => ['donations', 'detail', id] as const,
  deleted: (filters: DeletedDonationListInput) => ['donations', 'deleted', filters] as const,
  staffQuote: (input: StaffQuoteInput) => ['donations', 'staffQuote', input] as const,
};

export function useDonationRecordingInstitutions(search: string, enabled: boolean) {
  return useQuery({
    queryKey: [...donationKeys.all, 'recordingInstitutionOptions', search],
    queryFn: () =>
      api.donation.recordingInstitutionOptions({
        page: 1,
        pageSize: 20,
        search: search || undefined,
      }),
    enabled,
  });
}

export function useDonations(filters: DonationListInput) {
  return useQuery({
    queryKey: donationKeys.list(filters),
    queryFn: () => api.donation.list(filters),
    placeholderData: (previous) => previous,
  });
}

export function useDonation(id: string | undefined) {
  return useQuery({
    queryKey: donationKeys.detail(id ?? ''),
    queryFn: () => api.donation.get({ id: id as string }),
    enabled: Boolean(id),
  });
}

/**
 * The amount comes from the server, always. The browser never multiplies a unit
 * price by a quantity — docs/decisions/0013-campaign-pricing-and-currency.md —
 * and this is the staff sibling of the portal's public quote, so it also prices
 * an internal season and a closed one inside its backdating window.
 */
export function useStaffQuote(input: StaffQuoteInput | null) {
  return useQuery({
    queryKey: donationKeys.staffQuote(input ?? ({} as StaffQuoteInput)),
    queryFn: () => api.campaign.staffQuote(input as StaffQuoteInput),
    enabled: input !== null,
    retry: false,
  });
}

/**
 * The users the entry form may name as the operator, behind the entry form's
 * own `donation.recordForOperator` — asked only by a reader who holds it.
 */
export function useDonationOperatorOptions(
  search: string,
  enabled: boolean,
  scopeFilter?: { scopeLevel: 'zone' | 'country' | 'institution'; scopeId: string },
) {
  return useQuery({
    queryKey: [...donationKeys.all, 'operatorOptions', search, scopeFilter] as const,
    queryFn: () =>
      api.donation.operatorOptions({
        page: 1,
        pageSize: 20,
        search: search || undefined,
        ...scopeFilter,
      }),
    enabled,
  });
}

/**
 * A change of who the donation is recorded against moves the detail page, its
 * transitions and its timeline, and the operator the list shows.
 */
export function useChangeDonationOperator() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.donation.changeOperator>[0]) =>
      api.donation.changeOperator(input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: donationKeys.all }),
        queryClient.invalidateQueries({ queryKey: ['institutionLedger'] }),
      ]),
  });
}

/** The organizations a donation's main organization may be set to — read only while the dialog is open. */
export function useMainOrganizationOptions(donationId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...donationKeys.detail(donationId), 'main-organization-options'],
    queryFn: () => api.donation.mainOrganizationOptions({ id: donationId }),
    enabled,
  });
}

export function useSetMainOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.donation.setMainOrganization>[0]) =>
      api.donation.setMainOrganization(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: donationKeys.all }),
  });
}

export function useCreateDonation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.donation.create>[0]) => api.donation.create(input),
    // A save may have found or made the payer's and the owner's donors, so the
    // next entry's autocomplete must not answer from before it.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: donationKeys.all }),
        queryClient.invalidateQueries({ queryKey: ['donors'] }),
      ]),
  });
}

/**
 * A correction moves the record, its transitions and timeline, and the list
 * rows; a payer or *Bağış Sahibi* named by telephone may have found or made a
 * donor, so the donor lists are asked again as after an entry.
 */
export function useUpdateDonation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.donation.update>[0]) => api.donation.update(input),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: donationKeys.all }),
        queryClient.invalidateQueries({ queryKey: ['donors'] }),
      ]),
  });
}

/**
 * Who the donation's evidence is for and where each party's link stands. The
 * route is `donation.fulfill`, so a reader without it never asks — the refusal
 * would be a denial row on every load of the page. It hangs under the
 * donation's detail key, so one invalidation refreshes it with the record.
 */
export function useDonationEvidenceRecipients(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...donationKeys.detail(id), 'evidenceRecipients'] as const,
    queryFn: () => api.donation.evidenceRecipients({ id }),
    enabled: enabled && Boolean(id),
  });
}

type EvidenceRecipientInput = Parameters<typeof api.donation.evidenceResend>[0];

/**
 * A resend or an opt-out moves the party's link and preference, which the
 * evidence card and the record's own recipients both read — so the donation's
 * detail key, under which the card's read hangs, is invalidated.
 */
function useEvidenceRecipientMutation<Output>(
  run: (input: EvidenceRecipientInput) => Promise<Output>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: (_data, input) =>
      queryClient.invalidateQueries({ queryKey: donationKeys.detail(input.id) }),
  });
}

export function useResendEvidence() {
  return useEvidenceRecipientMutation((input) => api.donation.evidenceResend(input));
}

export function useOptOutOfEvidence() {
  return useEvidenceRecipientMutation((input) => api.donation.evidenceOptOut(input));
}

/**
 * *Bildirim İşlemleri* on the donation page — the four parties by the two
 * notices (docs/decisions/0080). Under the donation's detail key, so a change
 * to it, or to the evidence card beside it, refreshes both.
 */
export function useDonationNoticeChoices(id: string) {
  return useQuery({
    queryKey: [...donationKeys.detail(id), 'noticeChoices'] as const,
    queryFn: () => api.donation.noticeChoices({ id }),
    enabled: Boolean(id),
  });
}

export function useSetNoticeChoices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.donation.setNoticeChoices>[0]) =>
      api.donation.setNoticeChoices(input),
    onSuccess: (_data, input) =>
      queryClient.invalidateQueries({ queryKey: donationKeys.detail(input.id) }),
  });
}

export function useDonationTransitions(id: string | undefined) {
  return useQuery({
    queryKey: [...donationKeys.detail(id ?? ''), 'transitions'],
    queryFn: () => api.donation.transitions({ id: id as string }),
    enabled: Boolean(id),
  });
}

/**
 * The lifecycle mutations share one invalidation, because every one of them
 * moves a status the list, the tab counts and the detail all read.
 */
function useLifecycleMutation<I>(run: (input: I) => Promise<unknown>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: donationKeys.all }),
        queryClient.invalidateQueries({ queryKey: ['institutionLedger'] }),
      ]),
  });
}

/**
 * What a cancel would do, asked of the server when the dialog opens: a whole
 * cancellation, or only the shares not carried out, with the amounts and the
 * refund — docs/decisions/0074. The dialog shows this answer and computes none
 * of it.
 */
export function useDonationCancellationPreview(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...donationKeys.detail(id), 'cancellationPreview'],
    queryFn: () => api.donation.cancellationPreview({ id }),
    enabled,
    retry: false,
    staleTime: 0,
  });
}

export function useCancelDonation() {
  return useLifecycleMutation((input: Parameters<typeof api.donation.cancel>[0]) =>
    api.donation.cancel(input),
  );
}

/**
 * The trail for one donation, newest first. It reads `donation.timeline`, which
 * is guarded by `donation.view`, not the administrative `auditLog.list` —
 * whoever may open the record may read its history.
 */
export function useDonationTimeline(id: string | undefined) {
  return useQuery({
    queryKey: [...donationKeys.detail(id ?? ''), 'timeline'],
    queryFn: () => api.donation.timeline({ id: id as string, page: 1, pageSize: 50 }),
    enabled: Boolean(id),
  });
}

export const queueKeys = {
  all: ['donationQueue'] as const,
  list: (campaignPeriodId: string) => ['donationQueue', 'list', campaignPeriodId] as const,
};

/**
 * The ordered list of a period with its grouping already derived. The browser
 * never groups anything itself: `units_per_group` is configuration the server
 * applies to the order, so a screen that regrouped locally would be a second
 * copy of the rule — docs/decisions/0018-generic-fulfillment-queue.md.
 */
export function useDonationQueue(campaignPeriodId: string | undefined) {
  return useQuery({
    queryKey: queueKeys.list(campaignPeriodId ?? ''),
    queryFn: () => api.donation.queueList({ campaignPeriodId: campaignPeriodId as string }),
    enabled: Boolean(campaignPeriodId),
    placeholderData: (previous) => previous,
  });
}

/**
 * Queueing, fulfilling and reordering all move rows the donation list, its tab
 * counts and the period's totals read, so they share one invalidation.
 */
function useQueueMutation<I, R>(run: (input: I) => Promise<R>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queueKeys.all });
      void queryClient.invalidateQueries({ queryKey: donationKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['institutionLedger'] });
      void queryClient.invalidateQueries({ queryKey: ['campaignPeriods'] });
    },
  });
}

export function useQueueDonation() {
  return useQueueMutation((input: Parameters<typeof api.donation.queue>[0]) =>
    api.donation.queue(input),
  );
}

export function useDequeueDonation() {
  return useQueueMutation((input: Parameters<typeof api.donation.dequeue>[0]) =>
    api.donation.dequeue(input),
  );
}

export function useFulfillDonation() {
  return useQueueMutation((input: Parameters<typeof api.donation.fulfill>[0]) =>
    api.donation.fulfill(input),
  );
}

export function useBulkQueueDonations() {
  return useQueueMutation((input: Parameters<typeof api.donation.bulkQueue>[0]) =>
    api.donation.bulkQueue(input),
  );
}

/** Back to *Bekleyenler* for a selection: one call, a result per row. */
export function useBulkDequeueDonations() {
  return useQueueMutation((input: Parameters<typeof api.donation.bulkDequeue>[0]) =>
    api.donation.bulkDequeue(input),
  );
}

/**
 * Into the trash and out of it — docs/decisions/0078. A deletion and a restore
 * move the list, its tab counts, the queue, the period's totals and the trash,
 * so they share the queue's invalidation, which reaches all of them.
 */
export function useDeleteDonation() {
  return useQueueMutation((input: Parameters<typeof api.donation.delete>[0]) =>
    api.donation.delete(input),
  );
}

export function useBulkDeleteDonations() {
  return useQueueMutation((input: Parameters<typeof api.donation.bulkDelete>[0]) =>
    api.donation.bulkDelete(input),
  );
}

export function useRestoreDonation() {
  return useQueueMutation((input: Parameters<typeof api.donation.restore>[0]) =>
    api.donation.restore(input),
  );
}

/**
 * A deleted donation's own page — docs/decisions/0085. Its keys sit outside
 * `donationKeys.all` on purpose: every donation write invalidates that tree,
 * and a restore made from this page would otherwise refetch the page it is
 * leaving and read the donation it just restored as missing.
 */
export const deletedDonationKeys = {
  all: ['deletedDonation'] as const,
  detail: (id: string) => ['deletedDonation', id] as const,
  timeline: (id: string) => ['deletedDonation', id, 'timeline'] as const,
};

export function useDeletedDonation(id: string | undefined) {
  return useQuery({
    queryKey: deletedDonationKeys.detail(id ?? ''),
    queryFn: () => api.donation.getDeleted({ id: id as string }),
    enabled: Boolean(id),
  });
}

export function useDeletedDonationTimeline(id: string | undefined) {
  return useQuery({
    queryKey: deletedDonationKeys.timeline(id ?? ''),
    queryFn: () => api.donation.deletedTimeline({ id: id as string, page: 1, pageSize: 50 }),
    enabled: Boolean(id),
  });
}

export function useDeletedDonations(filters: DeletedDonationListInput) {
  return useQuery({
    queryKey: donationKeys.deleted(filters),
    queryFn: () => api.donation.listDeleted(filters),
    placeholderData: (previous) => previous,
  });
}

export function useFulfillDonationGroup() {
  return useQueueMutation((input: Parameters<typeof api.donation.fulfillGroup>[0]) =>
    api.donation.fulfillGroup(input),
  );
}

/** A drag sends the whole order, never a pair of swaps. */
export function useReorderQueue() {
  return useQueueMutation((input: Parameters<typeof api.donation.queueReorder>[0]) =>
    api.donation.queueReorder(input),
  );
}

export function useSortQueue() {
  return useQueueMutation((input: Parameters<typeof api.donation.queueSort>[0]) =>
    api.donation.queueSort(input),
  );
}

export function useCompactQueue() {
  return useQueueMutation((input: Parameters<typeof api.donation.queueCompact>[0]) =>
    api.donation.queueCompact(input),
  );
}

export function useGroupQueue() {
  return useQueueMutation((input: Parameters<typeof api.donation.queueGroup>[0]) =>
    api.donation.queueGroup(input),
  );
}

/**
 * Every file linked to the donation, for its *Medya / Kanıt Dosyaları* area —
 * docs/decisions/0118. The list signs nothing, so it may refetch freely.
 */
export function useDonationMedia(id: string | undefined) {
  return useQuery({
    queryKey: [...donationKeys.detail(id ?? ''), 'media'],
    queryFn: () => api.donation.media({ id: id as string }),
    enabled: Boolean(id),
  });
}
